import { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Trash, Pencil } from 'lucide-react';

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#6b7280', '#a3a3a3'];

export default function TagManager({ onClose }) {
  const { tags, createTag, updateTag, deleteTag } = useApp();
  const [name, setName] = useState('');
  const [color, setColor] = useState(COLORS[0]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');

  const handleCreate = async () => {
    if (!name.trim()) return;
    await createTag({ name: name.trim(), color });
    setName('');
    setColor(COLORS[0]);
  };

  const handleUpdate = async () => {
    if (!editName.trim() || !editingId) return;
    await updateTag(editingId, { name: editName.trim(), color: editColor });
    setEditingId(null);
  };

  const startEdit = (tag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="tag-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage Tags</DialogTitle>
          <DialogDescription>Create and organize your tags with colors.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tag name" className="flex-1 h-8 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} data-testid="tag-name-input" />
            <div className="flex gap-1">
              {COLORS.slice(0, 5).map((c) => (
                <button key={c} onClick={() => setColor(c)} className={`w-5 h-5 rounded-full border-2 transition-transform ${color === c ? 'border-foreground scale-110' : 'border-transparent'}`} style={{ backgroundColor: c }} />
              ))}
            </div>
            <Button onClick={handleCreate} size="sm" className="h-8" data-testid="tag-create-btn">Add</Button>
          </div>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {tags.map((tag) => (
              <div key={tag.id} className="flex items-center gap-2 py-1.5 px-2 rounded-sm hover:bg-muted/50" data-testid={`tag-item-${tag.id}`}>
                {editingId === tag.id ? (
                  <>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="flex-1 h-7 text-sm" onKeyDown={(e) => e.key === 'Enter' && handleUpdate()} />
                    <div className="flex gap-1">{COLORS.slice(0, 5).map((c) => (<button key={c} onClick={() => setEditColor(c)} className={`w-4 h-4 rounded-full border-2 ${editColor === c ? 'border-foreground' : 'border-transparent'}`} style={{ backgroundColor: c }} />))}</div>
                    <Button onClick={handleUpdate} size="sm" variant="ghost" className="h-7 text-xs">Save</Button>
                    <Button onClick={() => setEditingId(null)} size="sm" variant="ghost" className="h-7 text-xs">Cancel</Button>
                  </>
                ) : (
                  <>
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                    <span className="flex-1 text-sm">{tag.name}</span>
                    <button onClick={() => startEdit(tag)} className="p-1 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => deleteTag(tag.id)} className="p-1 text-muted-foreground hover:text-red-400"><Trash className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            ))}
            {tags.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No tags yet</p>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
